import { ls } from './storage';

type Handler = (msg: any) => void;
type StatusHandler = (connected: boolean) => void;

// One app-lifetime WebSocket, fanned out to many consumers (useStation, useCoder). Connects
// lazily on the first subscribe (so the token is in localStorage), never closes on zero
// subscribers, and replays channel subscriptions on every reconnect.
const handlers = new Set<Handler>();
const statusHandlers = new Set<StatusHandler>();
const channels = new Set<string>();
const sendQueue: string[] = [];
let listening: boolean | null = null;

let ws: WebSocket | null = null;
let connected = false;
let connecting = false;
let retry: ReturnType<typeof setTimeout> | null = null;

function setConnected(v: boolean): void {
  if (connected === v) return;
  connected = v;
  for (const h of statusHandlers) h(v);
}

function rawSend(obj: unknown): void {
  const data = JSON.stringify(obj);
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
  else sendQueue.push(data);
}

function connect(): void {
  if (connecting) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  connecting = true;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const token = ls.get('claudio.token');
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  let sock: WebSocket;
  try {
    sock = new WebSocket(`${proto}://${location.host}/stream${qs}`);
  } catch {
    connecting = false;
    setConnected(false);
    if (retry) clearTimeout(retry);
    retry = setTimeout(connect, 1500);
    return;
  }
  ws = sock;
  sock.onopen = () => {
    connecting = false;
    setConnected(true);
    for (const ch of channels) sock.send(JSON.stringify({ type: 'subscribe', channel: ch }));
    if (listening !== null) sock.send(JSON.stringify({ type: 'listening', on: listening }));
    while (sendQueue.length) sock.send(sendQueue.shift() as string);
  };
  sock.onclose = () => {
    connecting = false;
    setConnected(false);
    if (retry) clearTimeout(retry);
    retry = setTimeout(connect, 1500);
  };
  sock.onerror = () => sock.close();
  sock.onmessage = (e) => {
    let m: any;
    try {
      m = JSON.parse(e.data as string);
    } catch {
      return;
    }
    for (const h of handlers) h(m);
  };
}

export const wsBus = {
  subscribe(fn: Handler): () => void {
    handlers.add(fn);
    connect();
    return () => {
      handlers.delete(fn);
    };
  },
  subscribeStatus(fn: StatusHandler): () => void {
    statusHandlers.add(fn);
    fn(connected);
    return () => {
      statusHandlers.delete(fn);
    };
  },
  isConnected: (): boolean => connected,
  send: rawSend,
  setListening(on: boolean): void {
    listening = on;
    // only send directly when OPEN; onopen replays `listening` (avoids a stale duplicate via the queue)
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'listening', on }));
  },
  joinChannel(ch: string): void {
    channels.add(ch);
    // only send directly when OPEN; onopen replays `channels` (avoids a duplicate via the send queue)
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'subscribe', channel: ch }));
  },
  leaveChannel(ch: string): void {
    channels.delete(ch);
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'unsubscribe', channel: ch }));
  },
};
